import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from 'react';
import { ASSISTANT_CAPABILITIES, assistantRoleCan, findAssistantMenu } from '@cornerstone/shared';
import { api, type AssistantLink, type AssistantMessage, type AssistantPlanResult, type AssistantPlanStep } from '../client/api';
import { Button } from './ui';
import { describeApiError, toast } from './toast';
import { cn } from '../utils/cn';
import { ASSISTANT_PANEL_ID } from './AssistantFab';
import { allowedExamples, examplesFor, type AssistantExample } from './assistantPage';
import { readScope } from './GroupActivityHistory';
import { describeWorkRole } from '../labels/orgConcepts';
import type { GroupActor } from '../store/deviceGroups';

// =============================================================================
// AssistantPanel — natural-language schedule assistant.
//
// Every mutation goes through a visible plan card and an explicit 확인 click;
// the browser then calls the regular REST routes with the operator's own
// session, so permissions are exactly the dashboard's. Queries answer inline.
//
// Layout: a right-hand 400px panel on md+, a full-screen sheet on phones that
// follows the visual viewport so the on-screen keyboard never hides the input.
// =============================================================================

type PlanState = 'pending' | 'applied' | 'cancelled' | 'superseded';
type ChatEntry = AssistantMessage & {
  plan?: AssistantPlanResult['plan'];
  planState?: PlanState;
  /** When this entry's plan was issued; each plan ages on its own. */
  issuedAt?: number;
  resultIds?: Array<string | null>;
  links?: AssistantLink[];
};

/** Turns sent to the server per request (matches the planner window) and the
 *  on-screen cap so a long session never grows memory or payload unbounded. */
const HISTORY_SENT = 12;
const ENTRIES_KEPT = 200;
/** Textarea grows with its content up to about four lines. */
const INPUT_MAX_PX = 120;
const CAPABILITIES_ID = 'assistant-capabilities';

/** History line for an entry — a settled plan is folded into one sentence so
 *  the model can still refer to "the one I just added". */
function toHistory(en: ChatEntry): AssistantMessage {
  if (!en.plan) return { role: en.role, text: en.text };
  if (en.planState === 'pending') {
    // Full calls so the model can revise the plan ("13시 말고 14시로").
    const calls = en.plan.calls?.length ? JSON.stringify(en.plan.calls) : en.plan.steps.map((s) => s.label).join(' / ');
    return { role: 'assistant', text: `${en.text} [계획 확인 대기: ${calls}]` };
  }
  const state = en.planState === 'applied' ? '실행됨' : en.planState === 'cancelled' ? '취소됨' : '대체됨';
  const items = en.plan.steps.map((s, i) => `${s.label}${en.resultIds?.[i] ? ` id=${en.resultIds[i]}` : ''}`).join(' / ');
  return { role: 'assistant', text: `${en.text} [계획 ${state}: ${items}]` };
}

/** Plans older than this must be re-planned (state may have moved). */
const PLAN_TTL_MS = 10 * 60_000;

const media = (query: string) => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
/** Phones and small tablets get the full-screen sheet. */
export const isNarrowScreen = () => media('(max-width: 767px)');
/** Touch-first devices: never pop the keyboard up on their own. */
const isCoarsePointer = () => media('(pointer: coarse)');

/** Mobile sheet height/offset tracking the visual viewport (iOS keeps the layout
 *  viewport when the keyboard opens, so 100dvh alone would sit under it). */
function useSheetViewport(active: boolean): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties>();
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!active || !vv) { setStyle(undefined); return; }
    const update = () => setStyle({ height: `${vv.height}px`, top: `${vv.offsetTop}px` });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [active]);
  return style;
}

/** Group-workflow submissions are requests (they may still wait for approval); decisions and legacy writes apply. */
function appliedNote(steps: AssistantPlanStep[], done: number): { note: string; toastText: string } {
  const submitted = steps.slice(0, done).every((s) => s.path.startsWith('/api/group-workflow/') && !/\/(approve|reject)$/.test(s.path));
  return submitted
    ? { note: `요청 접수 (${done}건). 반영 여부는 계획 카드 안내를 따르고, 결과는 스케줄 > 승인 관리에서 확인하세요.`, toastText: '요청을 접수했습니다.' }
    : { note: `적용 완료 (${done}건).`, toastText: '적용되었습니다.' };
}

export function AssistantPanel({ open, onClose, onApplied, onNavigate, page, examples = examplesFor('other') }: {
  open: boolean;
  onClose: () => void;
  /** Called with the steps that actually ran (all on success, the prefix on partial failure). */
  onApplied: (steps: AssistantPlanStep[]) => void;
  /** Opens a menu shortcut. The path always comes from the shared menu guide. */
  onNavigate?: (path: string) => void;
  /** Current screen hint forwarded to the planner. */
  page?: string;
  /** Starter chips (page-specific ones come from AssistantHost); gated ones show only to roles that can run them. */
  examples?: readonly AssistantExample[];
}) {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiDisabled, setAiDisabled] = useState(false);
  const [showAll, setShowAll] = useState(false);
  /** Signed-in actor, re-read on every open: undefined while loading, null when unknown. Display only. */
  const [actor, setActor] = useState<GroupActor | null>();
  const [narrow, setNarrow] = useState(isNarrowScreen);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const sheetStyle = useSheetViewport(open && narrow);

  useEffect(() => {
    if (!open) return;
    let live = true;
    readScope().then((scope) => { if (live) setActor(scope.actor); }, () => { if (live) setActor(null); });
    return () => { live = false; };
  }, [open]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Keep the keyboard in the input on desktop: on open, and again after every
  // round trip. Touch devices only focus when the operator taps the field.
  useEffect(() => {
    if (open && !busy && !aiDisabled && !isCoarsePointer()) inputRef.current?.focus();
  }, [open, busy, aiDisabled]);

  // Phone sheet covers the page: stop the page behind it from scrolling.
  useEffect(() => {
    if (!open || !narrow) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, narrow]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
  }, [entries, busy, showAll]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight || 0, INPUT_MAX_PX) || 44}px`;
  }, [input, open]);

  const pendingPlan = entries.find((e) => e.planState === 'pending');

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy || aiDisabled) return;
    setInput('');
    setShowAll(false);
    const userTurn: ChatEntry = { role: 'user', text };
    const history: AssistantMessage[] = [...entries.map(toHistory), userTurn].slice(-HISTORY_SENT);
    setEntries((prev) => [...prev, userTurn].slice(-ENTRIES_KEPT));
    setBusy(true);
    try {
      const res = await api.assistant.plan(history, page);
      if (res.aiDisabled) setAiDisabled(true);
      setEntries((prev) => [
        // A new plan replaces the one still waiting for confirmation (revision flow).
        ...(res.plan ? prev.map((en) => (en.planState === 'pending' ? { ...en, planState: 'superseded' as const } : en)) : prev),
        {
          role: 'assistant', text: res.text,
          ...(res.plan ? { plan: res.plan, planState: 'pending' as const, issuedAt: Date.now() } : {}),
          ...(res.links?.length ? { links: res.links } : {}),
        },
      ]);
    } catch (err) {
      toast(describeApiError(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  function onInputKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter breaks the line; never send mid-composition (Korean IME).
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    void send();
  }

  function settlePlan(state: 'applied' | 'cancelled', note: string, resultIds?: Array<string | null>) {
    setEntries((prev) => [
      ...prev.map((en) => (en.planState === 'pending' ? { ...en, planState: state, ...(resultIds ? { resultIds } : {}) } : en)),
      { role: 'assistant', text: note },
    ]);
  }

  async function confirm(entry: ChatEntry) {
    const steps = entry.plan?.steps ?? [];
    if (!entry.issuedAt || Date.now() - entry.issuedAt > PLAN_TTL_MS) {
      settlePlan('cancelled', '계획이 오래되어 취소했습니다. 다시 요청해주세요.');
      return;
    }
    setBusy(true);
    let done = 0;
    const ids: Array<string | null> = [];
    try {
      for (const step of steps) {
        const result = (await api.assistant.run(step)) as { id?: unknown } | null;
        ids.push(typeof result?.id === 'string' ? result.id : null);
        done += 1;
      }
      const { note, toastText } = appliedNote(steps, done);
      settlePlan('applied', note, ids);
      toast(toastText, 'success');
      onApplied(steps);
    } catch (err) {
      const msg = describeApiError(err);
      settlePlan('cancelled', `${done + 1}번째 단계에서 실패: ${msg}${done ? ` (앞선 ${done}건은 적용됨)` : ''}`, ids);
      toast(msg, 'error');
      if (done) onApplied(steps.slice(0, done));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setEntries([]);
    setInput('');
  }

  function onPanelKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'Escape') { e.stopPropagation(); if (showAll) setShowAll(false); else onClose(); return; }
    // The phone sheet is modal: keep Tab inside it.
    if (e.key !== 'Tab' || !narrow || !panelRef.current) return;
    const focusable = [...panelRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), textarea:not([disabled]), [href]')];
    if (!focusable.length) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  if (!open) return null;

  const touch = 'min-h-11 min-w-11 justify-center md:min-h-0 md:min-w-0';
  const starters = allowedExamples(examples, actor?.role);

  return (
    <aside
      ref={panelRef}
      id={ASSISTANT_PANEL_ID}
      data-testid="assistant-panel"
      style={sheetStyle}
      onKeyDown={onPanelKeyDown}
      className={cn(
        'fixed flex flex-col bg-panel shadow-xl',
        // Phone: full-screen sheet above the bottom tab bar (z-40) and under toasts (z-70);
        // height follows the visual viewport when available.
        'z-[60] inset-x-0 top-0 h-[100dvh] w-full md:z-40',
        // md+: right-hand panel.
        'md:inset-x-auto md:right-0 md:h-full md:w-[400px] md:border-l md:border-line',
      )}
      role={narrow ? 'dialog' : 'complementary'}
      aria-modal={narrow || undefined}
      aria-label="어시스턴트"
    >
      <div className="flex-none flex items-center justify-between gap-2 border-b border-line px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] md:py-3 md:pt-3">
        <h2 className="text-[16px] md:text-[15px] font-semibold">어시스턴트</h2>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" className={cn(touch, 'text-[14px] max-md:px-2')} onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll} aria-controls={CAPABILITIES_ID}>할 수 있는 일 <span aria-hidden="true" className={cn('inline-block transition-transform duration-fast', showAll && 'rotate-180')}>▾</span></Button>
          <Button type="button" variant="ghost" className={cn(touch, 'text-[14px] max-md:px-2')} onClick={reset}
            disabled={busy || entries.length === 0} aria-label="새 대화">새 대화</Button>
          <Button type="button" className={touch} onClick={onClose} aria-label="닫기" data-modal-close>✕</Button>
        </div>
      </div>

      {showAll && (
        <CapabilityList actor={actor} disabled={aiDisabled || busy} onPick={(text) => {
          setInput(text);
          setShowAll(false);
          if (!isCoarsePointer()) inputRef.current?.focus();
        }} />
      )}

      {/* 목록이 열린 동안은 대화 자리를 통째로 쓴다(닫으면 위 effect가 맨 아래로 되돌린다). */}
      <div ref={scrollRef} className={cn('flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 flex flex-col gap-3 text-[15px] md:text-[14px]', showAll && 'hidden')}>
        {entries.length === 0 && (
          <div className="flex flex-col gap-2 text-text-dim">
            <p>스케줄을 말로 등록·수정하고, 현재 편성과 기기 상태를 물어보세요. 메뉴 위치나 사용법도 알려드려요. 변경은 계획을 먼저 보여주고 확인 후에만 실행됩니다.</p>
            <ExampleChips examples={starters} onPick={setInput} disabled={aiDisabled || busy} />
          </div>
        )}
        {entries.map((en, i) => (
          <div key={i} className={cn('flex flex-col gap-2', en.role === 'user' ? 'items-end' : 'items-start')}>
            <div className={cn('rounded-md px-3 py-2 max-w-[92%] break-words', en.role === 'user' ? 'bg-accent/15 whitespace-pre-wrap' : 'bg-bg border border-line')}>
              {en.role === 'assistant' ? <MessageText text={en.text} /> : en.text}
            </div>
            {en.links && en.links.length > 0 && (
              <MenuLinks links={en.links} onNavigate={onNavigate} className={touch} />
            )}
            {en.role === 'assistant' && !en.plan && en.text.startsWith('요청을 이해하지 못했어요') && (
              <ExampleChips examples={starters} onPick={setInput} disabled={aiDisabled || busy} />
            )}
            {en.plan && (
              <PlanCard plan={en.plan} state={en.planState ?? 'pending'} busy={busy} buttonClassName={touch}
                onConfirm={() => void confirm(en)}
                onCancel={() => settlePlan('cancelled', '취소했습니다.')} />
            )}
          </div>
        ))}
        {busy && <div className="text-text-dim" role="status">처리 중…</div>}
      </div>

      <form onSubmit={send} className="flex-none flex items-end gap-2 border-t border-line p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-3">
        <textarea
          ref={inputRef}
          rows={1}
          className="flex-1 min-h-11 resize-none rounded border border-line bg-bg px-3 py-2.5 text-[16px] leading-6 md:min-h-0 md:py-2 md:text-[14px] md:leading-5 disabled:opacity-50"
          style={{ maxHeight: INPUT_MAX_PX }}
          placeholder={aiDisabled ? 'AI 제공자가 설정되지 않았습니다' : pendingPlan ? '수정할 점을 말해주세요' : '요청을 입력하세요'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onInputKeyDown}
          disabled={aiDisabled}
          enterKeyHint="send"
          aria-label="어시스턴트 입력"
        />
        <Button type="submit" variant="primary" className={touch} disabled={aiDisabled || busy || !input.trim()}>보내기</Button>
      </form>
    </aside>
  );
}

type Block = { kind: 'p'; text: string } | { kind: 'ul' | 'ol'; items: string[] };

/** Minimal markdown: paragraphs, "- " bullets, "1." / "1)" numbered lists, **bold**. No HTML, no links. */
export function parseMessageBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const ul = line.match(/^[-*•·]\s+(.*)$/);
    const ol = line.match(/^\d{1,3}[.)]\s+(.*)$/);
    const kind = ul ? 'ul' : ol ? 'ol' : 'p';
    const item = (ul ?? ol)?.[1] ?? line;
    const last = blocks[blocks.length - 1];
    if (kind !== 'p' && last && last.kind === kind) last.items.push(item);
    else if (kind !== 'p') blocks.push({ kind, items: [item] });
    else blocks.push({ kind: 'p', text: line });
  }
  return blocks;
}

function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return <>{parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>))}</>;
}

function MessageText({ text }: { text: string }) {
  const blocks = parseMessageBlocks(text);
  return (
    <div className="flex flex-col gap-1.5">
      {blocks.map((b, i) =>
        b.kind === 'p' ? (
          // 목록 바로 앞의 "오프라인(6대):" 같은 줄은 소제목처럼 굵게, 앞 묶음과 간격.
          <p key={i} className={cn(b.text.endsWith(':') && (blocks[i + 1]?.kind ?? 'p') !== 'p' && 'font-semibold mt-1 first:mt-0')}><Inline text={b.text} /></p>
        ) : b.kind === 'ul' ? (
          <ul key={i} className="list-disc pl-5 flex flex-col gap-0.5">{b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}</ul>
        ) : (
          <ol key={i} className="list-decimal pl-5 flex flex-col gap-0.5">{b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}</ol>
        ),
      )}
    </div>
  );
}

/** Shortcut buttons. Unknown routeIds are dropped; the path comes from the shared guide only. */
function MenuLinks({ links, onNavigate, className }: { links: AssistantLink[]; onNavigate?: ((path: string) => void) | undefined; className?: string }) {
  const known = links.flatMap((l) => {
    const entry = findAssistantMenu(l.routeId);
    return entry ? [entry] : [];
  });
  if (!known.length || !onNavigate) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="menu-links">
      {known.map((entry) => (
        <Button key={entry.routeId} type="button" className={cn(className, 'text-[14px]')} onClick={() => onNavigate(entry.path)}>
          바로가기 · {entry.location}
        </Button>
      ))}
    </div>
  );
}

/** Everything this account can ask for, split into changes (plan → 확인) and questions. Role and
 *  group-permission mode filter it the way the planner decides; an unknown scope shows questions only. */
function CapabilityList({ actor, onPick, disabled }: { actor: GroupActor | null | undefined; onPick: (text: string) => void; disabled: boolean }) {
  const groupMode = !actor || actor.enforced;
  const usable = ASSISTANT_CAPABILITIES.filter((c) => !(groupMode && c.groupMode === false) && assistantRoleCan(actor?.role, c.permission));
  const sections = [
    { title: '바꾸기', hint: '계획을 먼저 보여주고, 확인해야 실행돼요', groups: usable.filter((c) => c.tools) },
    { title: '물어보기', hint: '바로 답해요. 아무것도 바뀌지 않아요', groups: usable.filter((c) => !c.tools) },
  ].filter((s) => s.groups.length > 0);
  return (
    <div id={CAPABILITIES_ID} role="region" aria-label="할 수 있는 일"
      className="flex-1 min-h-0 overflow-y-auto overscroll-contain break-keep bg-bg px-4 py-3 flex flex-col gap-4">
      {actor === undefined ? <p role="status" className="text-text-dim">권한 확인 중…</p> : (
        <>
          <p className="text-[13px] md:text-[12px] text-text-dim">
            {actor ? `지금 계정(${describeWorkRole(actor)})으로 쓸 수 있는 것만 보여요.` : '권한을 확인하지 못해 물어보기만 보여요.'}
          </p>
          {sections.map((s) => (
            <section key={s.title} aria-label={s.title} className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <h3 className="text-[14px] md:text-[13px] font-semibold">{s.title}</h3>
                <span className="text-[12px] text-text-dim">{s.hint}</span>
              </div>
              {s.groups.map((c) => (
                <div key={c.title} className="rounded-md border border-line bg-panel">
                  <div className="px-3 pt-2.5 pb-2">
                    <h4 className="text-[15px] md:text-[14px] font-semibold">{c.title}</h4>
                    <p className="mt-0.5 text-[13px] md:text-[12px] text-text-dim">{c.summary}</p>
                  </div>
                  <ul className="border-t border-line divide-y divide-line">
                    {c.examples.map((ex) => (
                      <li key={ex}>
                        <button type="button" disabled={disabled} onClick={() => onPick(ex)}
                          className="w-full min-h-11 px-3 py-2 text-left text-[14px] hover:bg-bg disabled:opacity-40 md:min-h-0 md:text-[13px]">{ex}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))}
          <p className="text-[13px] md:text-[12px] text-text-dim">
            <span className="font-semibold">안 되는 일</span> · 삭제, 플레이리스트 만들기·항목·순서 변경, 기기 해제는 하지 않아요. 물어보면 해당 메뉴에서 하는 방법을 알려드려요.
          </p>
        </>
      )}
    </div>
  );
}

function ExampleChips({ examples, onPick, disabled }: { examples: readonly string[]; onPick: (text: string) => void; disabled: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="example-chips">
      {examples.map((ex) => (
        <button
          key={ex}
          type="button"
          disabled={disabled}
          onClick={() => onPick(ex)}
          className="min-h-11 rounded-full border border-line px-3 py-1.5 text-left text-[14px] hover:border-line-strong hover:bg-panel disabled:opacity-40 md:min-h-0 md:px-2.5 md:py-1 md:text-[12px]"
        >
          {ex}
        </button>
      ))}
    </div>
  );
}

function PlanCard({ plan, state, busy, onConfirm, onCancel, buttonClassName }: {
  plan: NonNullable<AssistantPlanResult['plan']>;
  state: PlanState;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  buttonClassName?: string;
}) {
  return (
    <div className="w-full rounded-md border border-line-strong bg-bg p-3 flex flex-col gap-2" data-testid="plan-card">
      <div className="text-[14px] md:text-[13px] font-semibold">실행 계획 ({plan.steps.length}단계)</div>
      <ol className="list-decimal pl-5 flex flex-col gap-1 break-words">
        {plan.steps.map((s, i) => <li key={i}>{s.label}</li>)}
      </ol>
      {plan.warnings.length > 0 && (
        <ul className="rounded bg-warn/10 border border-warn/40 px-3 py-2 text-[14px] md:text-[13px] flex flex-col gap-1">
          {plan.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
        </ul>
      )}
      {state === 'pending' ? (
        <div className="flex gap-2 justify-end">
          <Button type="button" className={buttonClassName} onClick={onCancel} disabled={busy}>취소</Button>
          <Button type="button" variant="primary" className={buttonClassName} onClick={onConfirm} disabled={busy}>확인 후 실행</Button>
        </div>
      ) : (
        <div className="text-[12px] text-text-dim">{state === 'applied' ? '처리됨' : state === 'superseded' ? '새 계획으로 대체됨' : '취소됨'}</div>
      )}
    </div>
  );
}
